import { DeliveryStatus, SignatureRequestStatus } from 'constants/loanApplication';

export const DisclosureDeliveryHost = {
  UNDERWRITING: 'underwriting',
  DOCUMENTS_SUBTAB: 'documentsSubtab',
  WIZARD_POST_SEND: 'wizardPostSend',
};

export const DISCLOSURE_DELIVERY_POLL_INTERVAL_MS = 15000;
export const DISCLOSURE_DELIVERY_MAX_POLL_INTERVAL_MS = 60000;
export const DISCLOSURE_DELIVERY_POLL_BACKOFF_FACTOR = 2;

export const VerifyFactorType = {
  SSN: 'Ssn',
  ZIP: 'Zip',
};

export const RemediationActionType = {
  REISSUE_LINK: 'reissueLink',
  FIX_EMAIL: 'fixEmail',
  SWITCH_FACTOR: 'switchFactor',
  MARK_AS_MAILED: 'markAsMailed',
};

// Exact LoanActivityEntry.EventType strings the disclosure wizard writes (DEV-4626 Skynet.Loans
// attestations + Skynet.Forms remediation/mailed-delivery via the Loans client). Copied verbatim
// from the API contract (Part B3) — a typo splits the audit trail and breaks the eventTypes filter.
const DisclosureActivityEventType = {
  ATTESTATION_FEES_CURRENT: 'DisclosureAttestation.FeesCurrent',
  ATTESTATION_IDENTITY_VERIFIED: 'DisclosureAttestation.IdentityVerified',
  MAILED_DELIVERY: 'DisclosureMailedDelivery',
  REMEDIATION_LINK_REISSUED: 'DisclosureRemediation.LinkReissued',
  REMEDIATION_EMAIL_CORRECTED: 'DisclosureRemediation.EmailCorrected',
  REMEDIATION_FACTOR_SWITCHED: 'DisclosureRemediation.FactorSwitched',
};

export const DISCLOSURE_ACTIVITY_EVENT_TYPES = Object.values(DisclosureActivityEventType);

// The events that mark the send moment. There is no dedicated "sent" EventType, so "Sent by" is
// derived from the newest attestation actor at send time (contract Part B3 / task item 1).
export const DISCLOSURE_SEND_ATTESTATION_EVENT_TYPES = [
  DisclosureActivityEventType.ATTESTATION_FEES_CURRENT,
  DisclosureActivityEventType.ATTESTATION_IDENTITY_VERIFIED,
];

// Human-readable summary per event type for the "View audit trail" dialog. Falls back to the
// entry's own Description when an unknown type slips through.
export const DISCLOSURE_ACTIVITY_SUMMARY = {
  [DisclosureActivityEventType.ATTESTATION_FEES_CURRENT]:
    'Attested fees reflect the current lock/quote',
  [DisclosureActivityEventType.ATTESTATION_IDENTITY_VERIFIED]:
    'Attested borrower identity verified',
  [DisclosureActivityEventType.MAILED_DELIVERY]: 'Marked disclosure package as mailed',
  [DisclosureActivityEventType.REMEDIATION_LINK_REISSUED]: 'Reissued signing link',
  [DisclosureActivityEventType.REMEDIATION_EMAIL_CORRECTED]:
    'Corrected recipient email & resent notification',
  [DisclosureActivityEventType.REMEDIATION_FACTOR_SWITCHED]: 'Switched verification factor',
};

export const DISCLOSURE_ACTIVITY_MAX_ENTRIES = 50;

export const GENERATED_OUTSIDE_STATUS_LABEL = 'Prepared Outside Optimus';

export const GENERATED_OUTSIDE_TOOLTIP =
  'This package was generated and signed outside Optimus. Dates reflect the external record.';

export const GENERATED_OUTSIDE_SENT_TOOLTIP =
  'This package was generated outside Optimus and recorded as sent — it is not yet signed. Dates reflect the external record.';

// The status tag's ancestor table cell forces `white-space: nowrap` for compact rows and clips
// overflow (`overflow: hidden`); MuiTooltip runs with `disablePortal: true` (themesConfig.js), so
// this tooltip renders inside that same clipped subtree, inheriting the nowrap and getting cut off
// once wrapped. Force wrapping locally and escape the clip via a real portal.
export const GENERATED_OUTSIDE_TOOLTIP_SLOT_PROPS = {
  tooltip: { sx: { whiteSpace: 'normal' } },
  popper: { disablePortal: false },
};

const baseHostConfig = {
  sectionTitle: 'Form Delivery',
  showSectionSubtitle: false,
  showEcdChip: false,
  showAuditStrip: false,
  showManualRefresh: false,
  enablePolling: false,
  enableRowActions: false,
  enablePackageResend: false,
};

const DisclosureDeliveryHostConfig = {
  [DisclosureDeliveryHost.UNDERWRITING]: {
    ...baseHostConfig,
  },
  [DisclosureDeliveryHost.DOCUMENTS_SUBTAB]: {
    ...baseHostConfig,
    sectionTitle: 'Disclosures & Delivery',
    showSectionSubtitle: true,
    showEcdChip: true,
    showAuditStrip: true,
    showManualRefresh: true,
    enablePolling: true,
    enableRowActions: true,
    enablePackageResend: true,
  },
  [DisclosureDeliveryHost.WIZARD_POST_SEND]: {
    ...baseHostConfig,
    sectionTitle: 'Disclosures & Delivery',
    showSectionSubtitle: true,
    showAuditStrip: true,
    showManualRefresh: true,
    enablePolling: true,
    enableRowActions: true,
    enablePackageResend: true,
  },
};

export const getDisclosureDeliveryHostConfig = host =>
  DisclosureDeliveryHostConfig[host] ??
  DisclosureDeliveryHostConfig[DisclosureDeliveryHost.UNDERWRITING];

export const REVIEW_PENDING_PACKAGE_STATUSES = [
  SignatureRequestStatus.BORROWERS_PENDING,
  SignatureRequestStatus.LOAN_OFFICER_PENDING,
];

export const REVIEW_RECIPIENT_PENDING_STATUSES = [DeliveryStatus.SENT, DeliveryStatus.OPENED];
